import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraIconButton, SpecraSelect, SpecraTextarea } from '@mono/ui-components/ensigns';
import { SpecraFormField, SpecraInlineBanner, SpecraPopover } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { X } from 'lucide-react';
import { useRef, useState } from 'react';
import { RegistryEntityPicker } from '../../../components/SharedRecord/RegistryEntityPicker';
import { buildSignalTemplate, EMAIL_RE } from './rfiHomeModel';
import { useCreateRfiSignal } from './useRfiWorkspaceHome';
const T = Testids.rfiWorkspace;
const MAX = 1000;
function draftLabel(d) {
    return (d.name || [
        d.firstName,
        d.lastName
    ].filter(Boolean).join(' ') || d.contractNumber || 'record').trim();
}
/**
 * Post a signal (WRT-06 layout item 6, v2.3): the body is plain text with
 * "@mentions" picked from the registry (GRW-37 picker, createMode
 * "observation"); on submit the mentions become "{{n}}" placeholders. Text
 * typed without the picker is never resolved by name, and an email in the
 * body is refused (mention people with @ instead).
 */ export function RfiSignalComposer({ rfiResponseProjectId, linkedPursuits, onPosted }) {
    const [body, setBody] = useState('');
    const [mentions, setMentions] = useState([]);
    const [pursuit, setPursuit] = useState('');
    const [pickerOpen, setPickerOpen] = useState(false);
    const textRef = useRef(null);
    const create = useCreateRfiSignal(rfiResponseProjectId);
    const hasEmail = EMAIL_RE.test(body);
    const insertMention = (label, rest)=>{
        const ordinal = (mentions.reduce((m, x)=>Math.max(m, x.ordinal), 0) || 0) + 1;
        const el = textRef.current;
        const caret = el?.selectionStart ?? body.length;
        // Typing "@" opened the picker: replace that "@" instead of adding another.
        const before = body.slice(0, caret).replace(/@$/, '');
        const after = body.slice(caret);
        setBody(`${before}@${label} ${after}`.slice(0, MAX));
        setMentions((m)=>[
                ...m,
                {
                    ordinal,
                    label,
                    ...rest
                }
            ]);
        setPickerOpen(false);
    };
    const submit = ()=>{
        let template = buildSignalTemplate(body, mentions);
        const links = mentions.map((m)=>({
                ordinal: m.ordinal,
                linkRole: 'ABOUT',
                targetKind: m.targetKind,
                targetId: m.targetId ?? '',
                observation: m.observation ? {
                    kind: m.observation.kind,
                    name: m.observation.name ?? '',
                    firstName: m.observation.firstName ?? '',
                    lastName: m.observation.lastName ?? '',
                    title: m.observation.title ?? '',
                    uei: m.observation.uei ?? '',
                    cage: m.observation.cage ?? '',
                    orgCode: m.observation.orgCode ?? '',
                    agency: m.observation.agency ?? '',
                    contractNumber: m.observation.contractNumber ?? '',
                    // An email on a typed person is a strong id, sent only as the observation.
                    email: m.observation.email ?? ''
                } : undefined
            }));
        if (pursuit) {
            const ordinal = links.reduce((m, l)=>Math.max(m, l.ordinal), 0) + 1;
            template = `${template.trimEnd()} ({{${ordinal}}})`;
            links.push({
                ordinal,
                linkRole: 'RELEVANT_TO',
                targetKind: 'PURSUIT',
                targetId: pursuit,
                observation: undefined
            });
        }
        create.mutate({
            bodyTemplate: template,
            statusLabel: 'signal',
            links
        }, {
            onSuccess: ()=>{
                setBody('');
                setMentions([]);
                setPursuit('');
                onPosted();
            }
        });
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-[var(--space-3)]",
        "data-testid": T.signalComposer,
        children: [
            /*#__PURE__*/ _jsx(SpecraFormField, {
                hint: "Type @ to mention a person, company, office or vehicle from the registry.",
                htmlFor: "rfi-signal-body",
                label: "What did you learn?",
                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                    id: "rfi-signal-body",
                    maxLength: MAX,
                    onChange: (e)=>{
                        const v = e.target.value;
                        setBody(v);
                        const caret = e.target.selectionStart ?? v.length;
                        if (v.slice(0, caret).match(/(^|\s)@$/)) setPickerOpen(true);
                    },
                    ref: textRef,
                    rows: 4,
                    value: body
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-[var(--space-1-5)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": T.signalMention,
                        onClick: ()=>setPickerOpen(true),
                        size: "xs",
                        type: "button",
                        variant: "ghost",
                        children: "Mention"
                    }),
                    mentions.map((m)=>/*#__PURE__*/ _jsxs(SpecraBadge, {
                            className: "gap-[var(--space-1)]",
                            variant: "neutral",
                            children: [
                                "@",
                                m.label,
                                /*#__PURE__*/ _jsx(SpecraIconButton, {
                                    label: `Remove mention ${m.label}`,
                                    onClick: ()=>setMentions((all)=>all.filter((x)=>x.ordinal !== m.ordinal)),
                                    size: "sm",
                                    type: "button",
                                    variant: "ghost",
                                    children: /*#__PURE__*/ _jsx(X, {})
                                })
                            ]
                        }, m.ordinal))
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraPopover, {
                anchor: textRef.current,
                "aria-label": "Mention",
                onOpenChange: setPickerOpen,
                open: pickerOpen,
                children: /*#__PURE__*/ _jsx("div", {
                    className: "w-[min(24rem,80vw)]",
                    children: /*#__PURE__*/ _jsx(RegistryEntityPicker, {
                        context: {
                            kind: 'RFI',
                            id: rfiResponseProjectId
                        },
                        createMode: "observation",
                        kinds: [
                            'CONTACT',
                            'COMPANY',
                            'GOVERNMENT_ORG',
                            'VEHICLE'
                        ],
                        label: "Mention",
                        onCreateObservation: (draft)=>insertMention(draftLabel(draft), {
                                targetKind: draft.kind,
                                observation: draft
                            }),
                        onPick: (o)=>insertMention(o.label, {
                                targetKind: o.kind,
                                targetId: o.registryId
                            })
                    })
                })
            }),
            linkedPursuits.length > 0 ? /*#__PURE__*/ _jsx(SpecraFormField, {
                htmlFor: "rfi-signal-pursuit",
                label: "Link to",
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    id: "rfi-signal-pursuit",
                    onChange: (e)=>setPursuit(e.target.value),
                    value: pursuit,
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "",
                            children: "No pursuit"
                        }),
                        linkedPursuits.map((p)=>/*#__PURE__*/ _jsx("option", {
                                value: p.id,
                                children: p.title
                            }, p.id))
                    ]
                })
            }) : null,
            hasEmail ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "warning",
                children: "Mention people with @ instead of typing their email"
            }) : null,
            create.error ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "danger",
                children: create.error.message
            }) : null,
            /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end",
                children: /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": T.signalSubmit,
                    disabled: !body.trim() || hasEmail || create.isPending,
                    onClick: submit,
                    size: "sm",
                    children: "Post signal"
                })
            })
        ]
    });
}
